import { z } from "zod";
import { useAtom } from "jotai";
import { currentStep } from "@/app/store";
import { Button } from "@/view/components/ui/button";
import { differenceInYears, isBefore } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { crefisaService } from "@/app/services/crefisaService";
import { BasicInput } from "@/view/components/BasicInput";
import { BasicSelect } from "@/view/components/BasicSelect";
import { useFormContext } from "react-hook-form";
import { dateMask } from "@/app/utils/dateFormater";
import { phoneMask } from "@/app/utils/phoneFormater";
import { states } from "@/app/utils/states";
import { Spinner } from "@/view/components/ui/spinner";
import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { useEffect } from "react";
import masks from "@/app/utils/masks";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { Checkbox } from "@/view/components/ui/checkbox";
import { PartnerType } from "@/app/utils/partners.enum";

const ORG_PRIORITY = ["SSP", "DETRAN", "PC", "CGPI"];

const onlyDigitsInput = (e: React.FormEvent<HTMLInputElement>) => {
  const target = e.currentTarget;
  target.value = target.value.replace(/\D/g, "");
};

interface PersonalInfoFormProps {
  visible?: boolean;
}

export const personalFormSchema = z.object({
  name: z.string().min(1, "* obrigatório"),
  birthDate: z
    .string()
    .min(1, "* obrigatório")
    .refine((f) => {
      const [day, month, year] = f.split("/");
      const date = new Date(`${year}-${month}-${day}`);
      const diff = differenceInYears(new Date(), date);
      return diff >= 18;
    }, "O cliente deve ser maior de 18 anos")
    .transform((f) => {
      const [day, month, year] = f.split("/");
      return new Date(`${year}-${month}-${day}`).toISOString();
    }),
  cpf: z
    .string()
    .optional()
    .transform((v) => v?.replace(/\D/g, "") ?? ""),
  phone: z
    .string()
    .optional()
    .transform((v) => v?.replace(/\D/g, "") ?? ""),
  email: z.string().min(1, "* obrigatório").email("email invalido"),
  gender: z.string().min(1, "* obrigatório"),
  spouseName: z.string().optional(),
  motherName: z.string().min(1, "* obrigatório"),
  fatherName: z.string().optional(),
  authorizeSMS: z.boolean().optional(),
  placeOfBirth: z.object({
    city: z.string().min(1, "* obrigatório"),
    uf: z.string().min(1, "* obrigatório"),
  }),
  rg: z.object({
    number: z.string().min(1, "* obrigatório"),
    date: z
      .string()
      .min(1, "* obrigatório")
      .refine((f) => {
        const [day, month, year] = f.split("/");
        if (!day || !month || !year) return false;
        const date = new Date(`${year}-${month}-${day}`);
        return !isNaN(date.getTime()) && isBefore(date, new Date());
      }, "data inválida")
      .transform((f) => {
        const [day, month, year] = f.split("/");
        return new Date(`${year}-${month}-${day}`).toISOString();
      }),
    uf: z
      .string({ required_error: "* obrigatório" })
      .min(2, "* obrigatório")
      .max(2),
    org: z.string({ required_error: "* obrigatório" }).min(1, "* obrigatório"),
  }),
  educationLevel: z.number({
    required_error: "* obrigatório",
  }),
  maritalStatus: z.number({
    required_error: "* obrigatório",
  }),
  codeBenefitSpecie: z.number().optional(),
});

export default function PersonalInfoForm({ visible }: PersonalInfoFormProps) {
  const [, setStep] = useAtom(currentStep);

  const { customerData, setCustomer, consentBankAccount, partnerType } =
    useCrefisaContext();
  const isInss = partnerType === PartnerType.CREFISA_INSS;
  const { data: isCustomer } = useCustomerCpf(customerData?.cpf);

  const { data: complementsData, isLoading: isLoadingComplements } = useQuery({
    queryKey: ["crefisa-complements"],
    queryFn: () => crefisaService.getComplements(),
    select(data) {
      const sortedOrg = [...(data.orgaoEmissorRg ?? [])].sort((a, b) => {
        const aIndex = ORG_PRIORITY.indexOf(a.sigla);
        const bIndex = ORG_PRIORITY.indexOf(b.sigla);

        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });

      return {
        maritalStatus:
          data.estadoCivil.map((status) => ({
            label: status.descricao,
            value: status.estadoCivilId,
          })) ?? [],
        educationLevel:
          data.escolaridade.map((level) => ({
            label: level.descricao,
            value: level.escolaridadeId,
          })) ?? [],
        org: sortedOrg.map((org) => ({
          label: org.descricao,
          value: org.sigla,
        })),
        benefitSpecie: (data.especieBeneficio ?? []).map((specie) => ({
          label: specie.name,
          value: specie.value,
        })),
      };
    },
  });

  const {
    setValue,
    getValues,
    trigger,
    watch,
    setError,
    formState: { errors },
  } = useFormContext();

  const authorizeSMS = watch("personalInfo.authorizeSMS");

  const handleNext = async () => {
    const isValid = await trigger("personalInfo");

    if (isInss && getValues("personalInfo.codeBenefitSpecie") === undefined) {
      setError("personalInfo.codeBenefitSpecie", {
        message: "* obrigatório",
      });
      return;
    }

    if (isValid) {
      setStep((state) => state + 1);
    }
  };
  useEffect(() => {
    if (!isCustomer) return;

    setCustomer({
      ...isCustomer,
      id: isCustomer.id,
      ...(consentBankAccount ? { bankAccount: consentBankAccount } : {}),
    } as any);

    const setIfEmpty = (field: string, value?: string) => {
      if (value && !getValues(field)) {
        setValue(field, value);
      }
    };

    setIfEmpty("personalInfo.name", isCustomer.name);
    setIfEmpty("personalInfo.email", isCustomer.email);
    setIfEmpty(
      "personalInfo.birthDate",
      isCustomer.birthDate ? masks.date(isCustomer.birthDate) : undefined,
    );
    setIfEmpty("personalInfo.motherName", isCustomer.motherName);
    setIfEmpty("personalInfo.gender", isCustomer.gender);
    setIfEmpty("personalInfo.rg.number", isCustomer.rg?.number);
    setIfEmpty(
      "personalInfo.rg.date",
      isCustomer.rg?.date ? masks.date(isCustomer.rg.date) : undefined,
    );
    setIfEmpty("personalInfo.rg.uf", isCustomer.rg?.uf);
    setIfEmpty("personalInfo.rg.org", isCustomer.rg?.org);
    setIfEmpty("personalInfo.placeOfBirth.city", isCustomer.placeOfBirth?.city);
    setIfEmpty("personalInfo.placeOfBirth.uf", isCustomer.placeOfBirth?.uf);

    setIfEmpty("address.zipCode", isCustomer.address?.zipCode);
    setIfEmpty("address.street", isCustomer.address?.street);
    setIfEmpty("address.number", isCustomer.address?.number);
    setIfEmpty("address.complement", isCustomer.address?.complement);
    setIfEmpty("address.neighborhood", isCustomer.address?.neighborhood);
    setIfEmpty("address.city", isCustomer.address?.city);
    setIfEmpty("address.state", isCustomer.address?.state);
  }, [isCustomer, consentBankAccount]);

  useEffect(() => {
    if (customerData?.cpf) {
      setValue("personalInfo.cpf", masks.cpf(customerData.cpf));
    }
    if (customerData?.phone && !getValues("personalInfo.phone")) {
      setValue("personalInfo.phone", masks.phone(customerData.phone));
    }
  }, [customerData]);

  if (isLoadingComplements) {
    return <Spinner />;
  }

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BasicInput
          name="personalInfo.name"
          label="Nome completo"
          error={(errors.personalInfo as any)?.name?.message}
        />
        <BasicInput
          name="personalInfo.email"
          label="E-mail"
          error={(errors.personalInfo as any)?.email?.message}
        />
        <BasicInput
          name="personalInfo.phone"
          label="Telefone"
          error={(errors.personalInfo as any)?.phone?.message}
          onInput={phoneMask}
          maxLength={15}
        />
        <BasicInput
          name="personalInfo.cpf"
          label="CPF"
          // disabled
          error={(errors.personalInfo as any)?.cpf?.message}
        />
        <BasicInput
          type="text"
          name="personalInfo.birthDate"
          label="Data de nascimento"
          error={(errors.personalInfo as any)?.birthDate?.message}
          onInput={dateMask}
        />
        {/* TODO verificar api crefisa se aceita outros */}
        <BasicSelect
          name="personalInfo.gender"
          label="Gênero"
          error={(errors.personalInfo as any)?.gender?.message}
          options={[
            {
              label: "Masculino",
              value: "0",
            },
            {
              label: "Feminino",
              value: "1",
            },
          ]}
          placeholder="Selecione"
        />
        <BasicSelect
          name="personalInfo.educationLevel"
          label="Escolaridade"
          error={(errors.personalInfo as any)?.educationLevel?.message}
          options={complementsData?.educationLevel ?? []}
          placeholder="Selecione"
        />
        <BasicSelect
          name="personalInfo.maritalStatus"
          label="Estado civil"
          error={(errors.personalInfo as any)?.maritalStatus?.message}
          options={complementsData?.maritalStatus ?? []}
          placeholder="Selecione"
        />
        {isInss && (
          <BasicSelect
            name="personalInfo.codeBenefitSpecie"
            label="Espécie do benefício"
            error={(errors.personalInfo as any)?.codeBenefitSpecie?.message}
            options={complementsData?.benefitSpecie ?? []}
            placeholder="Selecione"
          />
        )}
        <BasicInput
          name="personalInfo.motherName"
          label="Nome da mãe"
          maxLength={35}
          error={(errors.personalInfo as any)?.motherName?.message}
        />
        <BasicInput
          name="personalInfo.fatherName"
          label="Nome do pai"
          maxLength={35}
          error={(errors.personalInfo as any)?.fatherName?.message}
        />
        <BasicInput
          name="personalInfo.placeOfBirth.city"
          label="Cidade de naturalidade"
          error={(errors.personalInfo as any)?.placeOfBirth?.city?.message}
        />
        <BasicSelect
          name="personalInfo.placeOfBirth.uf"
          label="UF de naturalidade"
          error={(errors.personalInfo as any)?.placeOfBirth?.uf?.message}
          options={states}
          placeholder="Selecione"
        />
        <BasicInput
          name="personalInfo.rg.number"
          label="RG"
          error={(errors.personalInfo as any)?.rg?.number?.message}
          inputMode="numeric"
          maxLength={11}
          onInput={onlyDigitsInput}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicInput
            type="text"
            name="personalInfo.rg.date"
            label="Data de emissão"
            error={(errors.personalInfo as any)?.rg?.date?.message}
            onInput={dateMask}
          />
          <BasicSelect
            label="UF de emissão"
            name="personalInfo.rg.uf"
            options={states}
            placeholder="Selecione o seu estado"
            error={(errors.personalInfo as any)?.rg?.uf?.message}
          />
        </div>
        <BasicSelect
          name="personalInfo.rg.org"
          label="Órgão emissor"
          error={(errors.personalInfo as any)?.rg?.org?.message}
          options={complementsData?.org ?? []}
          placeholder="Selecione"
        />
        <div className="mb-1 flex w-full items-center lg:col-span-2">
          <Checkbox
            id="authorizeSMS"
            checked={authorizeSMS}
            onCheckedChange={(v: boolean) =>
              setValue("personalInfo.authorizeSMS", v)
            }
          />

          <label htmlFor="authorizeSMS" className="ml-2">
            Autorizar receber SMS
          </label>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          type="button"
          onClick={handleNext}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
